<template>
	<v-sheet
			v-bind="$attrs"
			:ref="`ui-box-${_uid}`"
			class="ui-box pa-0 position-relative"
			:class="boxClass"
			v-on="$listeners"
			:style="{
				borderRadius: newForms ? '0 !important' : '8px !important',
				backgroundColor: `${bgColor} !important`,
				outline: newForms ? 'none !important' : `solid ${outlineWidth} ${outlineColor} !important`
			}"
	>
		<ui-box-status
				v-if="form || effectiveLocalStatus"
				:status="effectiveLocalStatus"
				:no-page-curl="newForms"
		/>
		<div
				v-if="header"
				class="font-weight-bold pa-4 ui-box__header"
				:class="headerClass"
		>
			{{ header }}
		</div>
		<div
				v-if="!header && $slots.header"
				:ref="`ui-box__header-${_uid}`"
				class="px-4 py-2 ui-box__header"
		>
			<slot name="header"/>
		</div>
		<div
				:ref="`ui-box__inner-${_uid}`"
				class="ui-box__inner"
				:class="{
						'border-bottom': footer || $slots.footer,
						'border-top': header || $slots.header,
						'has-status': effectiveLocalStatus,
						'container': noPadding || newForms,
						'container--fluid': (noPadding || newForms) && fluid,
						'pa-4': !noPadding && !newForms,
						'pa-0': noPadding,
						'px-0 py-4': newForms,
						[innerClasses]: !!innerClasses
				}"
				:style="{
					borderRadius: newForms ? '0 !important' : '6px !important',
					backgroundColor: `${bgColor} !important`
				}"
		>
			<slot name="default"/>
		</div>
		<div
				v-if="footer"
				:ref="`ui-box__footer-${_uid}`"
				class="pa-4 ui-box__footer"
				:class="footerClass"
		>
			{{ footer }}
		</div>
		<div v-if="!footer && $slots.footer" class="px-4 py-2 ui-box__footer">
			<slot name="footer"/>
		</div>
	</v-sheet>
</template>

<script>
/*
	Note: use class h-100 to stretch the box to fill the column it resides in
 */
export default {
	inheritAttrs: false,
	name: 'UiBox',
	props: {
		additionalValidations: {
			type: Array,
			default: () => []
		},
		backgroundColor: {
			type: String,
			default: '#FFFFFF'
		},
		borderColor: {
			type: String,
			default: '#e5e5e5'
		},
		borderWidth: {
			type: String,
			default: '1px'
		},

		// Eager would be used on edit forms, generally if we have an ID we
		// want to set the prop `eager` or other truthy state like `:eager="!!pass.id"`
		eager: Boolean,
		elevation: [Number, String],
		fluid: Boolean,
		footer: String,
		form: [Boolean, Object],
		header: String,
		innerClasses: String,
		isEditing: {
			type: Boolean,
			default: true
		},
		newForms: Boolean,
		noBorder: Boolean,
		noInnerBackground: Boolean,
		noPadding: Boolean,
		noPaddingX: Boolean,
		noPaddingY: Boolean,
		status: [Boolean, String],
		successOnly: Boolean,
		warnOnly: Boolean,
		noStatusBorder: Boolean
	},
	data() {
		return {
			boxClass: '',
			footerClass: '',
			headerClass: '',
			localStatus: null
		};
	},
	computed: {
		bgColor() {
			if(this.noInnerBackground) return 'transparent';
			if(/^#|^rgb|^var/.test(this.backgroundColor)) return this.backgroundColor;
			return `var(--v-${this.backgroundColor}-base)`;
		},
		effectiveLocalStatus() {
			return this.isEditing ? this.localStatus : null;
		},
		formHasInput() {
			const { form: formComponent } = this;
			if(!formComponent) return false;

			const nonCountryCodeInputs = formComponent.inputs.filter(i => !this.isCountryCode(i));

			if(nonCountryCodeInputs.length) {
				for (const input of nonCountryCodeInputs) {
					const isSwitch = this.isSwitch(input);
					if((isSwitch && input.hasFocused) || (!isSwitch && input.hasInput)) {
						return true;
					}
				}
			}
			return false;
		},
		formIsDirty() {
			const { form: formComponent } = this;
			if(!formComponent) return false;

			const nonCountryCodeInputs = formComponent.inputs.filter(i => !this.isCountryCode(i));

			if(nonCountryCodeInputs.length) {
				for (const input of nonCountryCodeInputs) {
					const isSwitch = this.isSwitch(input);
					if(isSwitch && input.hasFocused) {
						return true;
					}
					else if(!isSwitch && input.isDirty && input.hasInput) {
						return true;
					}
				}
			}
			return false;
		},
		outlineColor() {
			// No border overrides everything
			if(this.noBorder) return 'transparent';

			// Handle border statuses
			// if(this.form) {
			if(!this.noStatusBorder && this.effectiveLocalStatus) {
				return `var(--v-${this.localStatus}-base)`;
			}
			// }

			// Finally set border based on borderColor passed
			if(/^#|^rgb|^var/.test(this.borderColor)) return this.borderColor;
			return `var(--v-${this.borderColor}-base)`;
		},
		outlineWidth() {
			if(this.noBorder) return '0';
			return this.borderWidth;
		}
	},
	methods: {
		isObject(value) {
			return !Array.isArray(value)
					&& Object.prototype.toString.call(value) === '[object Object]';
		},
		isCountryCode(input) {
			return this.isObject(input.$props?.value)
					&& 'dialCode' in input.$props?.value || false;
		},
		isSwitch(input) {
			return input?.attrs?.role === 'switch' || false;
		},
		setLocalStatus(isValid = undefined) {
			const hasAdditionalValidations = !!this.additionalValidations.length;
			const hasFormInputs = !!this.form?.inputs?.length;
			const hasStatus = !!this.status;

			if(!hasAdditionalValidations && !hasFormInputs && !hasStatus) {
				this.localStatus = null;
				return;
			}

			this.$nextTick(() => {
				setTimeout(() => {
					const additionalValidation = this.additionalValidations.every(Boolean);
					let newStatus = this.localStatus;

					// 1. If there are no inputs to this form (probably image(s))
					if(!hasFormInputs) {
						newStatus = additionalValidation ? 'success' : 'error';
					}

					// 2. If there are inputs let's check if they're valid, but only if localStatus
					//    does not already = 'error' from above.
					//    Also, we only do this check if there are inputs, (e.g. a form with an 
					//    image only will not have actual inputs.
					//
					// 3. If we want to show status regardless of input
					if(this.formHasInput || this.eager) {
						newStatus = additionalValidation && isValid ? 'success' : 'error';
					}

					// 4. If we are only warning 'warnOnly: true'
					if(this.warnOnly && this.localStatus === 'error') {
						newStatus = 'warning';
					}

					// 5. If we are only showing success 'successOnly: true'
					if(this.successOnly && this.localStatus !== 'success') {
						newStatus = null;
					}

					// 6. If we are passing a status we must want to override all of the above
					if(hasStatus)
						newStatus = this.status;

					if(this.localStatus !== newStatus)
						this.localStatus = newStatus;

				}, 500);
			});
		}
	},
	mounted() {
		const mainContentEl = document.getElementById('main-content');
		const hasBg = mainContentEl.classList.contains('with-bg');
		const hasShadows = mainContentEl.classList.contains('with-shadows');
		const elevation = this.$props.elevation || (hasShadows ? 16 : 0);
		this.boxClass = `${hasBg ? '' : 'ui-box--no-bg '}elevation-${hasBg
				? 'green-'
				: ''}${elevation}`;
		this.boxClass = this.noBorder ? `${this.boxClass} no-border` : this.boxClass;

		this.headerClass = hasBg ? '' : 'ui-box__header--no-bg';
		this.footerClass = hasBg ? '' : 'ui-box__footer--no-bg';

		this.setLocalStatus();
	},
	watch: {
		additionalValidations: function () {
			if(this.form)
				this.setLocalStatus(this.form.$props.value);
		},
		formIsDirty: function () {
			this.setLocalStatus(this.form.$props.value);
		},
		'form.$props.value': function (isValid) {
			this.setLocalStatus(isValid);
		},
		status: function () {
			this.setLocalStatus();
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

.ui-box {
	backdrop-filter: blur(3px);
	background-color: rgba(255, 255, 255, 0.5) !important;
	padding: 2rem;

	&:not(.no-border) {
		outline: solid 1px #c9f6b1 !important;
	}

	&:not(:last-child) {
		margin-bottom: 2rem;
	}

	&__inner {
		&.has-status {
			::v-deep > div.row:first-child {
				margin-right: 4rem;

				@media #{map-get($display-breakpoints, 'sm-and-down')} {
					margin-right: 3.5rem;
				}
			}
		}
	}

	@media #{map-get($display-breakpoints, 'sm-and-down')} {
		padding: 1rem;

		&:not(:last-child) {
			margin-bottom: 1rem;
		}
	}
}

.bg-white {
	background: white;
}

.border-top {
	border-top: solid 1px #d1f1c1 !important;
}

.border-bottom {
	border-top: solid 1px #d1f1c1 !important;
}

#main-content:not(.with-main-bg) {
	.ui-box {

		&:not(.no-border) {
			outline: solid 1px #e5e5e5 !important;
		}

		& .ui-box__header,
		& .ui-box__footer {
			background-color: rgba(0, 0, 0, 0.02) !important;
			/*color: white;*/
		}

		& .ui-box__header {
			border-radius: 1rem 1rem 0 0 !important;
			margin-bottom: -1rem !important;
			padding-bottom: 2rem !important;
		}

		& .ui-box__footer {
			border-radius: 0 0 1rem 1rem !important;
			margin-top: -1rem !important;
			padding-top: 2rem !important;
		}
	}

	.border-top {
		border-top: solid 1px #dbdbdb !important;
	}

	.border-bottom {
		border-top: solid 1px #dbdbdb !important;
	}
}
</style>